import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  AudioLines,
  FileText,
  Mic,
  MicOff,
  Pause,
  Play,
  RotateCcw,
  Square,
} from "lucide-react";
import AppLayout from "../../../shared/components/AppLayout";
import Button from "../../../shared/components/Button";
import patientService from "../../../services/patientService";
import encountersService, {
  DocumentTemplateOption,
  TemplateId,
} from "../../../services/encountersService";
import { DoctorPatient } from "../../../types/patient";
import { RecordingResult } from "../../../types/transcript";
import { useDeepgramRecorder } from "../../../hooks/useDeepgramRecorder";
import {
  DEEPGRAM_LANGUAGES,
  DEFAULT_DEEPGRAM_LANGUAGE,
  DeepgramLanguage,
  findDeepgramLanguageByCode,
} from "../../../constants/deepgramLanguages";

// One bar per frequency band reported by the recorder hook.
const WAVE_BARS = Array.from({ length: 40 }, (_, i) => i);

const RecordingPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const patientId = id ? Number(id) : undefined;
  const navigate = useNavigate();

  const [patient, setPatient] = useState<DoctorPatient | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<DeepgramLanguage>(
    DEFAULT_DEEPGRAM_LANGUAGE,
  );

  const [templates, setTemplates] = useState<DocumentTemplateOption[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateId>("soap");

  const [result, setResult] = useState<RecordingResult | null>(null);
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);

  const transcriptRef = useRef<HTMLDivElement>(null);

  const {
    status,
    error,
    elapsedLabel,
    displayTranscriptText,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    getRecordingResult,
    canStart,
    canPause,
    canResume,
    canStop,
    isRecording,
    isPaused,
    isStoppingOrSaving,
    micBands,
    micInputDetected,
  } = useDeepgramRecorder({
    patientId,
    transcriptionLanguage: selectedLanguage,
    onFinalized: (r) => setResult(r),
  });

  useEffect(() => {
    if (!patientId) return;
    let cancelled = false;
    patientService
      .fetchDoctorPatient(patientId)
      .then((p) => !cancelled && setPatient(p))
      .catch(
        (e) =>
          !cancelled &&
          setLoadError(e instanceof Error ? e.message : "Patient not found"),
      );
    return () => {
      cancelled = true;
    };
  }, [patientId]);

  useEffect(() => {
    let cancelled = false;
    encountersService
      .fetchTemplates()
      .then((list) => {
        if (cancelled) return;
        setTemplates(list);
        // An admin can hide the built-in SOAP entry for a doctor, so the
        // initial "soap" selection may not be in their list. Fall back to the
        // flagged default, then to the first entry, so the switcher never sits
        // on an option the doctor cannot actually generate against.
        const def =
          list.find((t) => t.is_default) ??
          (list.some((t) => t.id === "soap") ? undefined : list[0]);
        if (def) setSelectedTemplate(def.id);
      })
      .catch(() => {
        /* templates are optional; default SOAP always works */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Keep the newest speech in view without growing the page — the transcript
  // panel is a fixed-height scroller, so the action bar never moves.
  useEffect(() => {
    const el = transcriptRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [displayTranscriptText]);

  const patientName = patient
    ? `${patient.first_name} ${patient.last_name}`.trim() || patient.name
    : "Patient";

  const languageLocked = status !== "idle" && status !== "error";

  const statusLabel = useMemo(() => {
    switch (status) {
      case "recording":
        return "RECORDING";
      case "paused":
        return "PAUSED";
      case "connecting":
      case "requesting_mic":
        return "CONNECTING";
      case "stopping":
        return "FINALIZING";
      case "stopped":
        return "READY TO GENERATE";
      case "error":
        return "ERROR";
      default:
        return "READY";
    }
  }, [status]);

  const statusTone = isRecording
    ? "bg-red-50 text-red-700 border-red-200"
    : isPaused
      ? "bg-amber-50 text-amber-700 border-amber-200"
      : status === "stopped"
        ? "bg-green-50 text-green-700 border-green-200"
        : "bg-gray-100 text-gray-600 border-gray-200";

  const statusDot = isRecording
    ? "bg-red-500"
    : isPaused
      ? "bg-amber-500"
      : status === "stopped"
        ? "bg-green-500"
        : "bg-gray-400";

  const handleGenerate = async () => {
    const finalized = result || getRecordingResult();
    if (!finalized || !finalized.transcriptText) {
      setGenerateError("No transcript was captured. Please record again.");
      return;
    }
    setGenerateError(null);
    setGenerating(true);
    try {
      const created = await encountersService.generateEncounter({
        transcript: finalized.transcriptText,
        audioBlob: finalized.audioBlob,
        audioFilename: `recording-${Date.now()}.webm`,
        patientId: patientId ?? null,
        templateId: selectedTemplate,
      });
      await encountersService.pollUntilComplete(created.id);
      navigate(`/encounters/${created.id}`);
    } catch (e: any) {
      setGenerateError(
        e?.response?.data?.error ||
          (e instanceof Error ? e.message : "Failed to generate the note."),
      );
      setGenerating(false);
    }
  };

  if (generating) {
    return (
      <AppLayout active="patients">
        <div className="max-w-md mx-auto flex flex-col items-center justify-center py-24 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-6" />
          <h1 className="text-lg font-semibold text-gray-900">
            Generating the note…
          </h1>
          <p className="text-sm text-gray-500 mt-2">
            Transcribing and drafting the documentation. This can take a minute
            — you'll be redirected when it's ready.
          </p>
        </div>
      </AppLayout>
    );
  }

  const selectClass =
    "w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:bg-gray-50 disabled:text-gray-500";
  const labelClass =
    "block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5";

  return (
    // padding="none" because this screen manages its own full-height grid; the
    // layout chrome (nav + sidebar) is identical to every other page.
    <AppLayout active="patients" padding="none">
      <div className="h-full flex flex-col min-h-0 p-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 mb-5 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <Button
              variant="ghost"
              size="sm"
              icon={ArrowLeft}
              onClick={() =>
                navigate(patientId ? `/patients/${patientId}` : "/patients")
              }
              className="-ml-2"
            >
              Back
            </Button>
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wider text-gray-400">
                Consultation
              </p>
              <h1 className="text-lg font-semibold text-gray-900 truncate">
                {patientName}
              </h1>
            </div>
          </div>

          <div
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold tracking-wide ${statusTone}`}
          >
            <span
              className={`w-2 h-2 rounded-full ${statusDot} ${
                isRecording ? "animate-pulse" : ""
              }`}
            />
            {statusLabel}
          </div>
        </div>

        {loadError && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm shrink-0">
            {loadError}
          </div>
        )}

        {/* Controls left, transcript right. min-h-0 lets each column scroll. */}
        <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[minmax(0,400px)_minmax(0,1fr)] gap-6">
          {/* ── Controls ─────────────────────────────── */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex flex-col min-h-0 overflow-y-auto">
            <div className="text-center">
              <div
                className={`text-5xl font-light tabular-nums tracking-tight ${
                  isRecording ? "text-gray-900" : "text-gray-400"
                }`}
              >
                {elapsedLabel}
              </div>
            </div>

            {/* Waveform — each bar is its own frequency band, so the shape
                changes with the voice instead of one scalar scaling a fixed
                silhouette. No CSS transition here: the values already arrive
                smoothed at ~30fps, and animating them again just adds lag. */}
            <div className="mt-6 h-16 flex items-end justify-center gap-[3px]">
              {WAVE_BARS.map((i) => {
                const band = micBands[i] ?? 0;
                const h = isRecording ? Math.max(3, band * 60) : 3;
                return (
                  <div
                    key={i}
                    className={`w-[3px] rounded-full ${
                      isRecording && band > 0.05 ? "bg-blue-500" : "bg-gray-200"
                    }`}
                    style={{ height: `${h}px` }}
                  />
                );
              })}
            </div>

            {/* Mic state */}
            <div className="mt-2 flex items-center justify-center gap-2 text-xs">
              {isRecording && !micInputDetected ? (
                <>
                  <MicOff size={13} className="text-amber-600" />
                  <span className="text-amber-600">No mic input detected</span>
                </>
              ) : (
                <>
                  <Mic
                    size={13}
                    className={isRecording ? "text-green-600" : "text-gray-400"}
                  />
                  <span
                    className={isRecording ? "text-green-600" : "text-gray-400"}
                  >
                    {isRecording ? "Mic active" : "Microphone idle"}
                  </span>
                </>
              )}
            </div>

            {error && (
              <p className="mt-4 text-center text-sm text-red-600">{error}</p>
            )}

            {/* Transport */}
            <div className="mt-7 flex items-center justify-center gap-4">
              {canStart && (
                <button
                  type="button"
                  onClick={() => startRecording()}
                  disabled={!!loadError || isStoppingOrSaving}
                  className="w-20 h-20 rounded-full bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-600/30 flex items-center justify-center transition-all hover:scale-105 active:scale-95 disabled:opacity-40 disabled:hover:scale-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
                  aria-label="Start recording"
                >
                  <Mic size={30} />
                </button>
              )}

              {canPause && (
                <button
                  type="button"
                  onClick={pauseRecording}
                  disabled={isStoppingOrSaving}
                  className="w-14 h-14 rounded-full bg-white border-2 border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-50 flex items-center justify-center transition-colors disabled:opacity-40"
                  aria-label="Pause recording"
                >
                  <Pause size={20} />
                </button>
              )}

              {canResume && (
                <button
                  type="button"
                  onClick={resumeRecording}
                  disabled={isStoppingOrSaving}
                  className="w-14 h-14 rounded-full bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center transition-colors disabled:opacity-40"
                  aria-label="Resume recording"
                >
                  <Play size={20} />
                </button>
              )}

              {canStop && status !== "idle" && (
                <button
                  type="button"
                  onClick={() => stopRecording()}
                  disabled={isStoppingOrSaving}
                  className="w-20 h-20 rounded-full bg-gray-900 hover:bg-gray-800 text-white shadow-lg flex items-center justify-center transition-all hover:scale-105 active:scale-95 disabled:opacity-40"
                  aria-label="Stop recording"
                >
                  <Square size={24} fill="currentColor" />
                </button>
              )}
            </div>

            {/* Settings */}
            <div className="mt-8 space-y-4">
              <div>
                <label htmlFor="encounter-language" className={labelClass}>
                  Spoken language
                </label>
                <select
                  id="encounter-language"
                  value={selectedLanguage.code}
                  disabled={languageLocked}
                  onChange={(e) => {
                    const lang = findDeepgramLanguageByCode(e.target.value);
                    if (lang) setSelectedLanguage(lang);
                  }}
                  className={selectClass}
                >
                  {DEEPGRAM_LANGUAGES.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.label}
                    </option>
                  ))}
                </select>
              </div>

              {templates.length > 0 && (
                <div>
                  <label htmlFor="encounter-template" className={labelClass}>
                    Documentation template
                  </label>
                  <select
                    id="encounter-template"
                    value={String(selectedTemplate)}
                    onChange={(e) => {
                      const v = e.target.value;
                      setSelectedTemplate(v === "soap" ? "soap" : Number(v));
                    }}
                    className={selectClass}
                  >
                    {templates.map((t) => (
                      <option key={String(t.id)} value={String(t.id)}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* ── Transcript ───────────────────────────── */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col min-h-0">
            <div className="flex items-center gap-2 px-5 py-3.5 border-b border-gray-200 shrink-0">
              <AudioLines size={16} className="text-blue-600" />
              <h2 className="text-sm font-semibold text-gray-900">
                Live transcript
              </h2>
              {isRecording && (
                <span className="ml-auto text-[11px] text-gray-400">
                  transcribing…
                </span>
              )}
            </div>

            {/* The only scrolling region for transcript text, so a long
                conversation never pushes the action bar off-screen. */}
            <div
              ref={transcriptRef}
              className="flex-1 min-h-0 overflow-y-auto px-5 py-4"
            >
              <p
                className={`text-[15px] leading-relaxed whitespace-pre-wrap ${
                  displayTranscriptText
                    ? "text-gray-800"
                    : "text-gray-400 italic"
                }`}
              >
                {displayTranscriptText ||
                  (isRecording || isPaused || status === "connecting"
                    ? "Listening…"
                    : "Press record to start the consultation. The transcript will appear here in real time.")}
              </p>
            </div>

            {/* Action bar pinned to the bottom of the column. */}
            <div className="border-t border-gray-200 px-5 py-4 shrink-0 bg-gray-50 rounded-b-xl">
              {generateError && (
                <p className="mb-3 text-sm text-red-600">{generateError}</p>
              )}
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-gray-500">
                  {status === "stopped"
                    ? "Recording finished — ready to generate."
                    : "Stop the recording to generate the note."}
                </p>
                <div className="flex items-center gap-2">
                  {status === "stopped" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={RotateCcw}
                      onClick={() => startRecording()}
                    >
                      Record again
                    </Button>
                  )}
                  <Button
                    size="lg"
                    icon={FileText}
                    onClick={handleGenerate}
                    disabled={status !== "stopped"}
                  >
                    Generate note
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default RecordingPage;
